import type React from "react";
import { useState, useEffect } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Grid,
	Snackbar,
	Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { ErrorModal } from "@dexteel/mesf-core";
import { useWorkOrderModalsContext } from "@/shared/work-orders-modals/context/work-order-modals.context";
import {
	linkWorkOrderLOTO,
	unLinkWorkOrderLOTO,
} from "@/pages/work-orders/repositorie/WorkOrdersRepositorie";
import type { WorkOrderLOTO } from "../../../../models/WorkOrderLOTO";
import { AssetLOTOTable } from "./asset-LOTO-table";

interface Props {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	WorkOrderId: number | null;
	WorkOrderName: string;
	Title: string;
	AssetName: string;
}

export const AssetLOTOPicker: React.FC<Props> = ({
	show,
	onHide,
	WorkOrderId,
	WorkOrderName,
	Title,
	AssetName,
}) => {
	const {
		state: { workOrderLOTOList },
	} = useWorkOrderModalsContext();

	const [selectedLOTO, setSelectedLOTO] = useState<WorkOrderLOTO[]>([]);
	const [error, setError] = useState("");
	const [successMessage, setSuccessMessage] = useState("");

	useEffect(() => {
		if (show) {
			setSelectedLOTO(
				workOrderLOTOList.filter(
					(WorkOrderLOTO) => WorkOrderLOTO.IsAssetResource,
				),
			);
		}
	}, [show, workOrderLOTOList]);

	const handleSelectionChanged = (newSelectedLOTO: WorkOrderLOTO[]) => {
		setSelectedLOTO(newSelectedLOTO);
	};

	const getNewlyAddedLOTO = (
		currentSelected: WorkOrderLOTO[],
		originalList: WorkOrderLOTO[],
	): WorkOrderLOTO[] => {
		return currentSelected.filter(
			(selectedLOTO) =>
				!originalList.some(
					(existingLOTO) =>
						existingLOTO.LOTONumber === selectedLOTO.LOTONumber &&
						existingLOTO.IsAssetResource,
				),
		);
	};

	const getRemovedLOTO = (
		currentSelected: WorkOrderLOTO[],
		originalList: WorkOrderLOTO[],
	): WorkOrderLOTO[] => {
		return originalList.filter(
			(originalLOTO) =>
				originalLOTO.IsAssetResource &&
				!currentSelected.some(
					(selectedLOTO) => selectedLOTO.LOTONumber === originalLOTO.LOTONumber,
				),
		);
	};

	const handleSave = async () => {
		const newlyAddedLOTO = getNewlyAddedLOTO(selectedLOTO, workOrderLOTOList);
		const removedLOTO = getRemovedLOTO(selectedLOTO, workOrderLOTOList);

		if (newlyAddedLOTO.length > 0) {
			const newAssetLOTO = newlyAddedLOTO.map((WorkOrderLOTO) => ({
				LOTONumber: WorkOrderLOTO.LOTONumber,
				LOTOName: WorkOrderLOTO.LOTOName,
				IsAssetResource: true,
			}));
			await linkWorkOrderLOTO(WorkOrderId as number, newAssetLOTO as any);
			onHide(true);
		} else onHide(false);

		if (removedLOTO.length > 0) {
			const assetLOTOToRemoved = removedLOTO.map((WorkOrderLOTO) => ({
				LOTONumber: WorkOrderLOTO.LOTONumber,
				IsAssetResource: true,
			}));
			await unLinkWorkOrderLOTO(
				WorkOrderId as number,
				assetLOTOToRemoved as any,
			);
			onHide(true);
		} else onHide(false);
	};

	return (
		<>
			<Dialog open={show} onClose={() => onHide(false)} maxWidth="md" fullWidth>
				<DialogTitle style={{ padding: "24px 24px 0" }}>
					<Typography variant="h5" style={{ margin: "0", fontWeight: 600 }}>
						LOTO FROM ASSET
					</Typography>
					<Typography
						style={{
							display: "flex",
							alignItems: "center",
							fontSize: "1.3rem",
							flexWrap: "wrap",
							gap: "8px",
							padding: "8px 0 0",
						}}
					>
						<div
							style={{
								display: "inline-flex",
								alignItems: "center",
								whiteSpace: "nowrap",
								margin: "4px 0",
							}}
						>
							<span style={{ fontWeight: 600 }}>Work Order:</span>
							<span style={{ marginLeft: "4px" }}>{WorkOrderName}</span>
						</div>
						<div
							style={{
								display: "inline-flex",
								alignItems: "center",
								whiteSpace: "nowrap",
								margin: "4px 8px",
							}}
						>
							|
						</div>
						<div
							style={{
								display: "inline-flex",
								alignItems: "center",
								whiteSpace: "nowrap",
								margin: "4px 0",
							}}
						>
							<span style={{ fontWeight: 600 }}>Title:</span>
							<span style={{ marginLeft: "4px" }}>{Title}</span>
						</div>
						<div
							style={{
								display: "inline-flex",
								alignItems: "center",
								whiteSpace: "nowrap",
								margin: "4px 8px",
							}}
						>
							|
						</div>
						<div
							style={{
								display: "inline-flex",
								alignItems: "center",
								whiteSpace: "nowrap",
								margin: "4px 0",
							}}
						>
							<span style={{ fontWeight: 600 }}>Asset:</span>
							<span style={{ marginLeft: "4px" }}>{AssetName}</span>
						</div>
					</Typography>
				</DialogTitle>
				<DialogContent>
					<AssetLOTOTable
						workOrderLOTO={workOrderLOTOList.filter(
							(WorkOrderLOTO) => WorkOrderLOTO.IsAssetResource,
						)}
						onSelectionChanged={handleSelectionChanged}
					/>
				</DialogContent>
				<DialogActions style={{ padding: "16px 24px 24px" }}>
					<Grid container spacing={2} justifyContent="flex-end">
						<Grid item>
							<Button
								variant="contained"
								color="default"
								onClick={() => onHide(false)}
							>
								Cancel
							</Button>
						</Grid>
						<Grid item>
							<Button variant="contained" color="primary" onClick={handleSave}>
								Save
							</Button>
						</Grid>
					</Grid>
				</DialogActions>
			</Dialog>
			<ErrorModal error={error} onHide={() => setError("")} />
			<Snackbar
				open={!!successMessage}
				autoHideDuration={6000}
				onClose={() => setSuccessMessage("")}
			>
				<Alert onClose={() => setSuccessMessage("")} severity="success">
					{successMessage}
				</Alert>
			</Snackbar>
		</>
	);
};
